export class Scheduler {
    constructor(logger, playbackLogger, displayId) {
        this.logger = logger;
        this.playbackLogger = playbackLogger;
        this.displayId = displayId;
        this.currentlyPlaying = null;
        this.preloaded = null;
        this.idleMode = false;
        this.holdingForId = null;
    }
    schedule(currentlyPlaying, preloaded, player, isDownloading = false, dateDiff = null, 
    // the item for the current slot when it exists but is still downloading (a prepared one comes as currentlyPlaying)
    unpreparedCurrent = null) {
        // 1) cp = cp && pr = pr -> no change
        if (this.currentlyPlaying?.id === currentlyPlaying?.id &&
            this.preloaded?.id === preloaded?.id &&
            (currentlyPlaying !== null || preloaded !== null || this.idleMode)) {
            return; // nothing changed
        }
        // 1b) cp = null && uc != null && this.cp inside its slot -> hold: keep this.cp on screen (no stop/idle, state untouched)
        //     instead of going blank for the download; once uc is prepared it arrives as cp and case 4 switches. Ends at this.cp.to.
        if (!currentlyPlaying && unpreparedCurrent && this.currentlyPlaying) {
            const now = this.adjustDateToDiff(new Date(), dateDiff);
            if (now >= unpreparedCurrent.from && now >= this.currentlyPlaying.from && now < this.currentlyPlaying.to) {
                if (this.holdingForId !== unpreparedCurrent.id) {
                    this.holdingForId = unpreparedCurrent.id;
                    this.logger.debug('[Scheduler] Keeping current item on screen until the new item for this slot is prepared', {
                        currentlyPlayingId: this.currentlyPlaying.id,
                        unpreparedCurrentId: unpreparedCurrent.id,
                        unpreparedPlayableId: unpreparedCurrent.playable.id,
                        holdUntil: this.currentlyPlaying.to,
                    });
                }
                return;
            }
        }
        // 1c) the held-for item got prepared with too little of its slot left to be preloaded -> keep holding instead of
        //     force-stopping into a blank tail; this.cp plays out its slot and case 4 takes over after this.cp.to as before.
        if (currentlyPlaying && this.currentlyPlaying && this.holdingForId === currentlyPlaying.id) {
            const now = this.adjustDateToDiff(new Date(), dateDiff);
            if (now < this.currentlyPlaying.to && !this.hasEnoughTimeToPreload(currentlyPlaying, now)) {
                this.logger.debug('[Scheduler] New item prepared too late to preload, keeping current item until the end of its slot', {
                    currentlyPlayingId: this.currentlyPlaying.id,
                    newItemId: currentlyPlaying.id,
                    holdUntil: this.currentlyPlaying.to,
                });
                return;
            }
        }
        this.holdingForId = null;
        // there has to be some change
        // cases that was already handled:
        // 1) cp = cp && pr = pr
        // 1b) cp = null && uc != null && this.cp inside its slot (hold)
        // 1c) cp = held-for item && too late to preload it (hold continues)
        // 2) cp = null && pr = null -> idle
        if (!currentlyPlaying && !preloaded) {
            // idle
            this.currentlyPlaying = null;
            this.preloaded = null;
            if (isDownloading) {
                if (!this.idleMode) {
                    player.idle(true, null);
                    this.idleMode = true;
                }
                return;
            }
            this.logger.debug('[Scheduler] No scheduled items, idling player', { isDownloading, currentlyPlaying, preloaded });
            if (!this.idleMode) {
                player.idle(false, null);
                this.idleMode = true;
            }
            return;
        }
        // cases that was already handled:
        // 1) cp = cp && pr = pr
        // 1b) cp = null && uc != null && this.cp inside its slot (hold)
        // 1c) cp = held-for item && too late to preload it (hold continues)
        // 2) cp = null && pr = null
        this.idleMode = false;
        // both not null from here on
        // 3) cp = pr && pr != pr -> preload next item as there was a shift
        if (currentlyPlaying?.id === this.preloaded?.id && preloaded?.id !== this.preloaded?.id) {
            this.currentlyPlaying = currentlyPlaying;
            // If we're in a gap (currentlyPlaying hasn't started yet), don't preload
            // the next item — it would overwrite the current item's preloaded state
            // before its play timer fires. Leave this.preloaded unchanged so the
            // next tick handles it via case 5.
            const now = this.adjustDateToDiff(new Date(), dateDiff);
            if (currentlyPlaying && now < currentlyPlaying.from) {
                return;
            }
            this.preloaded = preloaded;
            if (preloaded) {
                this.preloadIfThereIsEnoughTime(player, preloaded, dateDiff);
            }
            return;
        }
        // cases that was already handled:
        // 1) cp = cp && pr = pr
        // 1b) cp = null && uc != null && this.cp inside its slot (hold)
        // 1c) cp = held-for item && too late to preload it (hold continues)
        // 2) cp = null && pr = null
        // 3) cp = pr && pr != pr
        // there was not a standard scheduling shift, handle complex cases as the eventlist must been regenerated
        // 4) cp != pr && cp != cp
        if (currentlyPlaying?.id !== this.preloaded?.id && this.currentlyPlaying?.id !== currentlyPlaying?.id) {
            if (this.currentlyPlaying) {
                player.stop(this.currentlyPlaying, new Date(), true);
                this.currentlyPlaying = null;
            }
            this.preloaded = currentlyPlaying;
            if (currentlyPlaying) {
                this.preloadIfThereIsEnoughTime(player, currentlyPlaying, dateDiff);
            }
            return; // item will be player on next schedule run
        }
        // cases that was already handled:
        // 1) cp = cp && pr = pr
        // 1b) cp = null && uc != null && this.cp inside its slot (hold)
        // 1c) cp = held-for item && too late to preload it (hold continues)
        // 2) cp = null && pr = null
        // 3) cp = pr && pr != pr
        // 4) cp != pr && cp != cp
        if (currentlyPlaying?.id === this.currentlyPlaying?.id && preloaded?.id !== this.preloaded?.id) {
            // only preloaded changed
            this.preloaded = preloaded;
            if (!this.currentlyPlaying) {
                player.idle(!!preloaded, preloaded?.from ?? null); // idle changed
            }
            if (preloaded) {
                this.preloadIfThereIsEnoughTime(player, preloaded, dateDiff);
            }
            return;
        }
        // cases that was already handled:
        // 1) cp = cp && pr = pr
        // 1b) cp = null && uc != null && this.cp inside its slot (hold)
        // 1c) cp = held-for item && too late to preload it (hold continues)
        // 2) cp = null && pr = null
        // 3) cp = pr && pr != pr
        // 4) cp != pr && cp != cp
        // 5) cp = cp && pr != pr
        // there should not be any other case possible, log warning if it happens (items have different id (eventlist items) and when new eventlist is generated, then all items have new ids, items are sorted by time which means the items will have different increasing ids)
        this.logger.warning('[Scheduler] Unhandled scheduling case', {
            currentlyPlaying,
            preloaded,
            thisCurrentlyPlaying: this.currentlyPlaying,
            thisPreloaded: this.preloaded,
            idleMode: this.idleMode,
            isDownloading,
        });
    }
    preloadIfThereIsEnoughTime(player, item, dateDiff = null) {
        const now = this.adjustDateToDiff(new Date(), dateDiff);
        if (!this.hasEnoughTimeToPreload(item, now)) {
            this.logger.debug('[Scheduler] Not enough time to preload item, skipping preload', { item, remainingTime: item.to.getTime() - now.getTime(), now });
            return;
        }
        const from = this.adjustDateToDiff(item.from, dateDiff);
        if (this.playbackLogger) {
            this.playbackLogger.debug(`${from.toISOString().split('T')[1]}: ${item.playable.name}@${this.displayId}`);
        }
        player.queue(item, from, this.adjustDateToDiff(item.to, dateDiff));
    }
    // less than 3 s of the slot left (5 s for video/audio) is not worth a preload
    hasEnoughTimeToPreload(item, now) {
        const remainingTime = Math.abs(item.to.getTime() - now.getTime());
        const isAv = item.playable.type === 'video' || item.playable.type === 'audio';
        return remainingTime >= (isAv ? 5000 : 3000);
    }
    getStatusSummary() {
        return {
            currentlyPlayingId: this.currentlyPlaying?.playable?.id ?? null,
            preloadedId: this.preloaded?.playable?.id ?? null,
            idleMode: this.idleMode,
        };
    }
    adjustDateToDiff(date, diff) {
        if (!diff) {
            return date;
        }
        return new Date(date.getTime() + diff);
    }
}
//# sourceMappingURL=Scheduler.js.map